import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import dayjs from "dayjs";
import { AgGridReact } from "ag-grid-react";
import { ColDef, IGetRowsParams } from "ag-grid-community";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

import usePermission from "../../hooks/usePermission";
import { FeatureStatic } from "../../@types/statics/feature.static";
import { IKeyValue } from "../../@types/key-value.type";
import { AuditActionEnum, IAuditFilter, IAuditLogGrid } from "../../@types/audit.type";
import {
  getAuditableEntities,
  getAuditActions,
  searchAuditLogs,
} from "../../services/api/audit/AuditService";
import { Chip, TextField } from "../../ui";
import { themeGrid } from "../../themeGrid";
import { localeText } from "../../localeTextFromGrid";
import { AuditDetailModal } from "./components/AuditDetailModal";
import styles from "./styles.module.scss";

// The backend's AllowedOrderByFields whitelist only recognizes these exact
// PascalCase property names via EF.Property (case-sensitive reflection) -
// deriving "UserName" from the colId with a generic camelCase->PascalCase
// regex breaks on multi-word ids (e.g. "userName" -> "Username", which slips
// past the whitelist's case-insensitive check but doesn't match the real
// CLR property, so EF.Property throws), so it's mapped explicitly instead.
const orderByFieldByColId: Record<string, string> = {
  timestamp: "Timestamp",
  userName: "UserName",
};

const periodPresets: { label: string; getRange: () => { startDate: string; endDate: string } }[] = [
  {
    label: "Hoje",
    getRange: () => {
      const today = dayjs().format("YYYY-MM-DD");
      return { startDate: today, endDate: today };
    },
  },
  {
    label: "Ontem",
    getRange: () => {
      const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
      return { startDate: yesterday, endDate: yesterday };
    },
  },
  {
    label: "Última semana",
    getRange: () => ({
      startDate: dayjs().subtract(6, "day").format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
    }),
  },
];

const columnsDefault: ColDef[] = [
  {
    field: "timestamp",
    headerName: "Data/Hora",
    flex: 1,
    minWidth: 170,
    sortable: true,
    valueFormatter: (params) => dayjs(params.value).format("DD/MM/YYYY HH:mm:ss"),
  },
  {
    field: "userName",
    headerName: "Usuário",
    flex: 1,
    minWidth: 160,
    sortable: true,
  },
  {
    field: "moduleName",
    headerName: "Módulo",
    flex: 1,
    minWidth: 140,
    sortable: false,
  },
  {
    field: "entityDisplayName",
    headerName: "Registro",
    flex: 1,
    minWidth: 180,
    sortable: false,
  },
  {
    field: "actionDescription",
    headerName: "Resumo",
    flex: 2,
    minWidth: 260,
    sortable: false,
  },
];

export const Auditoria: React.FC = () => {
  const navigate = useNavigate();
  const permissions = usePermission(FeatureStatic.audit.id);
  const gridRef = useRef<AgGridReact<IAuditLogGrid>>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [actions, setActions] = useState<IKeyValue[]>([]);
  const [entities, setEntities] = useState<IKeyValue[]>([]);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  const getFromCache = (key: string) => {
    const data = secureLocalStorage.getItem(key);
    return data ? JSON.parse(data as string) : null;
  };

  const [filter, setFilter] = useState<Partial<IAuditFilter>>(() => {
    // Módulo/Ação/Busca podem vir do cache (preferência do usuário entre sessões), mas o
    // período sempre nasce em "hoje" nesta carga da página - senão um cache de um dia
    // anterior (ou de antes deste atalho existir) trava o filtro nessa data para sempre.
    const cached = getFromCache("agGridFiltersAuditoria") ?? {};
    const today = dayjs().format("YYYY-MM-DD");
    return { ...cached, startDate: today, endDate: today };
  });

  const saveFilterToCache = (data: Partial<IAuditFilter>) => {
    secureLocalStorage.setItem("agGridFiltersAuditoria", JSON.stringify(data));
  };

  const filterRef = useRef(filter);
  filterRef.current = filter;

  const updateFilter = (partial: Partial<IAuditFilter>) => {
    const next = { ...filter, ...partial };
    setFilter(next);
    saveFilterToCache(next);
    gridRef.current?.api.refreshInfiniteCache();
  };

  const loadLookups = async () => {
    try {
      const [actionsResponse, entitiesResponse] = await Promise.all([
        getAuditActions(),
        getAuditableEntities(),
      ]);
      setActions(actionsResponse.result);
      setEntities(entitiesResponse.result);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!permissions.canView()) {
      navigate("/unauthorized");
      return;
    }

    loadLookups();
  }, []);

  // Memoized with a stable identity: AgGridReact's infinite row model treats a new
  // `datasource` object reference as a brand new datasource and resets/re-fetches -
  // recreating it on every render (as a plain function call in JSX would) combined
  // with setLoading() triggering a render on every fetch caused an infinite loop of
  // resets that never gave the grid a chance to actually paint the rows. Reading the
  // filter through filterRef (instead of closing over the `filter` state) keeps this
  // datasource valid across filter changes without needing to recreate it.
  const dataSource = useMemo(
    () => ({
      getRows: async (params: IGetRowsParams) => {
        const sortModel = params.sortModel;

        const requestFilter: IAuditFilter = {
          ...filterRef.current,
          skip: params.startRow,
          take: params.endRow - params.startRow,
        };

        if (sortModel.length > 0) {
          requestFilter.orderByField = orderByFieldByColId[sortModel[0].colId];
          requestFilter.orderByDirection = sortModel[0].sort === "asc" ? 0 : 1;
        }

        try {
          setLoading(true);
          const response = await searchAuditLogs(requestFilter);
          params.successCallback(response.result.items, response.result.totalRows);
        } catch (error) {
          console.error("Erro ao buscar registros de auditoria:", error);
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    }),
    []
  );

  return (
    <main className={styles.auditoria}>
      <div className={styles.title}>
        <h1>Auditoria</h1>
      </div>

      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <TextField
            label="De"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filter.startDate ?? ""}
            onChange={(e) => updateFilter({ startDate: e.target.value || undefined })}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filter.endDate ?? ""}
            onChange={(e) => updateFilter({ endDate: e.target.value || undefined })}
          />
          <div className={styles.periodShortcuts}>
            {periodPresets.map((preset) => {
              const range = preset.getRange();
              const isActive = filter.startDate === range.startDate && filter.endDate === range.endDate;
              return (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  size="small"
                  clickable
                  color={isActive ? "primary" : "default"}
                  onClick={() => updateFilter(range)}
                />
              );
            })}
          </div>
        </div>
        <div className={styles.filterRow}>
          <FormControl size="small" className={styles.select}>
            <InputLabel>Módulo</InputLabel>
            <Select
              label="Módulo"
              value={filter.entityName ?? ""}
              onChange={(e) => updateFilter({ entityName: (e.target.value as string) || undefined })}
            >
              <MenuItem value="">Todos</MenuItem>
              {entities.map((entity) => (
                <MenuItem key={entity.key} value={entity.key}>
                  {entity.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" className={styles.select}>
            <InputLabel>Ação</InputLabel>
            <Select
              label="Ação"
              value={filter.action ?? ""}
              onChange={(e) => updateFilter({ action: (e.target.value as AuditActionEnum) || undefined })}
            >
              <MenuItem value="">Todas</MenuItem>
              {actions.map((action) => (
                // action.key is the enum MEMBER NAME as text (e.g. "Create", from the backend's
                // nameof(AuditActionEnum.Create)), not a stringified number - AuditActionEnum[key]
                // uses the TS numeric enum's reverse mapping to resolve it to the actual numeric value
                // the backend's AuditFilterDto.Action expects.
                <MenuItem key={action.key} value={AuditActionEnum[action.key as keyof typeof AuditActionEnum]}>
                  {action.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Buscar"
            size="small"
            placeholder="Texto livre..."
            className={styles.searchField}
            value={filter.searchText ?? ""}
            onChange={(e) => updateFilter({ searchText: e.target.value || undefined })}
          />
        </div>
      </div>

      <div className={styles.dataGridContainer}>
        <AgGridReact
          ref={gridRef}
          theme={themeGrid}
          localeText={localeText}
          loading={loading}
          columnDefs={columnsDefault}
          overlayNoRowsTemplate="<span style='color: gray;'>Nenhum registro de auditoria encontrado</span>"
          defaultColDef={{ sortable: false, editable: false, filter: false }}
          suppressCellFocus={true}
          rowStyle={{ cursor: "pointer" }}
          onRowClicked={(row) => setSelectedLogId(row.data.id)}
          cacheBlockSize={20}
          rowModelType="infinite"
          datasource={dataSource}
          animateRows={true}
        />
      </div>

      {selectedLogId && (
        <AuditDetailModal
          auditLogId={selectedLogId}
          open={!!selectedLogId}
          onClose={() => setSelectedLogId(null)}
        />
      )}
    </main>
  );
};
